import { useState, type CSSProperties, type FormEvent } from 'react';
import {
  ArrowRight,
  BrainCircuit,
  Eye,
  EyeOff,
  LineChart,
  Lock,
  Shield,
  ShieldCheck,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import BrandLogo from '@/components/common/BrandLogo';
import { cn } from '@/lib/utils';

interface AuthenticationPageProps {
  onSubmit: (
    username: string,
    password: string,
    remember: boolean,
  ) => Promise<boolean>;
}

const FEATURES = [
  {
    icon: BrainCircuit,
    title: 'Smart Test Analysis',
    description: 'AI-powered insights from your test artifacts',
  },
  {
    icon: ShieldCheck,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security with Keycloak authentication',
  },
  {
    icon: LineChart,
    title: 'Unified Insights',
    description: '360° view of quality across your delivery pipeline',
  },
] as const;

const BRAND_BLUE = '#0196ff';
const BRAND_BLUE_RGB = '1, 150, 255';
const BRAND_BG_FROM = '#0170d4';
const BRAND_BG_MID = '#014fa0';
const BRAND_BG_TO = '#01336b';

const AuthenticationPage = ({ onSubmit }: AuthenticationPageProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [forgotHint, setForgotHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!username.trim() || !password.trim() || submitting) {
      return;
    }

    setSubmitting(true);
    setError('');
    setForgotHint(false);

    try {
      const accepted = await onSubmit(username.trim(), password, remember);
      if (!accepted) {
        setError('Invalid username or password. Please try again.');
        setPassword('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = username.trim().length > 0 && password.trim().length > 0;

  return (
    <div
      className="fixed inset-0 overflow-hidden text-white"
      style={{ backgroundColor: BRAND_BG_MID }}>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08), transparent 35%), radial-gradient(circle at 80% 0%, rgba(${BRAND_BLUE_RGB}, 0.18), transparent 30%), linear-gradient(135deg, ${BRAND_BG_FROM} 0%, ${BRAND_BG_MID} 45%, ${BRAND_BG_TO} 100%)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[38%] opacity-35"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(${BRAND_BLUE_RGB}, 0.35) 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
          maskImage: 'linear-gradient(to top, black, transparent)',
        }}
        aria-hidden
      />

      <div className="relative z-[1] mx-auto flex h-full min-h-0 w-full max-w-[1100px] items-center overflow-hidden px-4 py-3 sm:px-5 lg:gap-8 lg:px-8 lg:py-4 xl:max-w-[1200px] xl:gap-10 xl:px-10 xl:py-5">
        <section className="hidden min-h-0 flex-1 flex-col justify-center overflow-y-auto overscroll-contain lg:flex lg:pr-4">
          <div className="mb-5 flex items-center gap-3 xl:mb-8">
            <BrandLogo className="h-8 brightness-0 invert xl:h-9" />
          </div>

          <p className="mb-5 max-w-md text-[0.95rem] leading-relaxed text-blue-100/90 xl:mb-8 xl:text-[1.05rem]">
            AI-Powered Test Intelligence for Modern Teams
          </p>

          <ul className="m-0 flex max-w-lg list-none flex-col gap-3 p-0 xl:gap-4">
            {FEATURES.map(feature => {
              const Icon = feature.icon;
              return (
                <li key={feature.title} className="flex items-start gap-3 xl:gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15 xl:h-11 xl:w-11">
                    <Icon className="h-4 w-4 text-blue-100 xl:h-5 xl:w-5" strokeWidth={1.75} />
                  </span>
                  <span>
                    <span className="block text-[0.875rem] font-semibold text-white xl:text-[0.95rem]">
                      {feature.title}
                    </span>
                    <span className="mt-0.5 block text-[0.8125rem] leading-relaxed text-blue-100/75 xl:mt-1 xl:text-[0.875rem]">
                      {feature.description}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-y-auto overscroll-contain lg:justify-end">
          <div className="my-auto w-full max-w-[min(100%,320px)] shrink-0 rounded-2xl bg-white px-4 py-4 shadow-[0_20px_50px_-18px_rgba(15,23,42,0.4)] sm:max-w-[340px] sm:px-5 sm:py-5 2xl:max-w-[380px] 2xl:px-6 2xl:py-6">
            <div className="mb-3 flex justify-center">
              <div className="relative flex h-10 w-10 items-center justify-center">
                <span
                  className="absolute inset-0 rounded-full border border-dashed border-[#66b8ff]/70"
                  aria-hidden
                />
                <span
                  className="absolute inset-[0.35rem] rounded-full border border-[#99d1ff]/80"
                  aria-hidden
                />
                <span
                  className="relative flex h-8 w-8 items-center justify-center rounded-full shadow-[0_0_0_6px_rgba(1,150,255,0.12)]"
                  style={{ backgroundColor: BRAND_BLUE }}>
                  <Lock
                    className="h-4 w-4 text-white"
                    strokeWidth={2.25}
                    aria-hidden
                  />
                </span>
              </div>
            </div>

            <div className="mb-3 text-center sm:mb-4">
              <h1 className="m-0 text-[1.2rem] font-semibold leading-tight tracking-[-0.02em] text-[#0f2337] sm:text-[1.3rem]">
                Welcome to{' '}
                <span style={{ color: BRAND_BLUE }}>QTraceAI</span>
              </h1>
              <p className="m-0 mt-1 text-[0.75rem] text-[#64748b] sm:text-[0.8125rem]">
                Sign in to continue to your account
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-2.5 sm:space-y-3">
              <div>
                <label
                  htmlFor="auth-username"
                  className="mb-1 block text-[0.75rem] font-medium text-[#334155] sm:text-[0.8125rem]">
                  Username
                </label>
                <div className="relative">
                  <User
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <input
                    id="auth-username"
                    type="text"
                    autoFocus
                    autoComplete="username"
                    placeholder="Enter your username"
                    value={username}
                    disabled={submitting}
                    aria-invalid={Boolean(error)}
                    onChange={event => {
                      setUsername(event.target.value);
                      if (error) setError('');
                    }}
                    className={cn(
                      'box-border h-9 w-full rounded-lg border border-solid bg-[#f8fafc] py-1.5 pl-9 pr-3 text-[0.8125rem] text-[#0f172a] outline-none transition-[border-color,box-shadow] sm:h-10 sm:pl-10 sm:text-sm',
                      'placeholder:text-[#94a3b8]',
                      'focus-visible:ring-2 focus-visible:ring-[rgba(1,150,255,0.15)]',
                      error
                        ? 'border-[#fecaca]'
                        : 'border-[#e2e8f0]',
                      'focus-visible:border-[#0196ff]',
                    )}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="auth-password"
                  className="mb-1 block text-[0.75rem] font-medium text-[#334155] sm:text-[0.8125rem]">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <input
                    id="auth-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    disabled={submitting}
                    aria-invalid={Boolean(error)}
                    onChange={event => {
                      setPassword(event.target.value);
                      if (error) setError('');
                    }}
                    className={cn(
                      'box-border h-9 w-full rounded-lg border border-solid bg-[#f8fafc] py-1.5 pl-9 pr-9 text-[0.8125rem] text-[#0f172a] outline-none transition-[border-color,box-shadow] sm:h-10 sm:pl-10 sm:pr-10 sm:text-sm',
                      'placeholder:text-[#94a3b8]',
                      'focus-visible:ring-2 focus-visible:ring-[rgba(1,150,255,0.15)]',
                      error
                        ? 'border-[#fecaca]'
                        : 'border-[#e2e8f0]',
                      'focus-visible:border-[#0196ff]',
                    )}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={submitting}
                    onClick={() => setShowPassword(value => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 border-0 bg-transparent p-0 text-[#94a3b8] transition-colors hover:text-[#475569]">
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                </div>
              </div>

              <div
                className="flex items-center justify-between gap-3 pt-1"
                style={
                  {
                    '--inv-accent': BRAND_BLUE,
                    '--inv-accent-ring': `rgba(${BRAND_BLUE_RGB}, 0.2)`,
                    '--inv-on-accent': '#ffffff',
                    '--inv-border-strong': '#cbd5e1',
                    '--inv-surface': '#ffffff',
                  } as CSSProperties
                }>
                <Checkbox
                  id="auth-remember"
                  checked={remember}
                  disabled={submitting}
                  onChange={setRemember}
                  className="text-[#475569] [&_span]:text-[0.75rem] sm:[&_span]:text-[0.8125rem]">
                  Remember me
                </Checkbox>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setForgotHint(true)}
                  className="border-0 bg-transparent p-0 text-[0.75rem] font-medium hover:opacity-90 sm:text-[0.8125rem]"
                  style={{ color: BRAND_BLUE }}>
                  Forgot password?
                </button>
              </div>

              {forgotHint ? (
                <p className="m-0 text-[0.8125rem] leading-relaxed text-[#64748b]">
                  Contact your QTrace AI administrator to reset your password.
                </p>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="m-0 text-[0.8125rem] font-medium text-[#dc2626]">
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                className="mt-0.5 h-9 w-full rounded-lg border-0 bg-[#0196ff] text-[0.8125rem] font-semibold text-white shadow-none hover:bg-[#017ecc] disabled:opacity-60 sm:h-10 sm:text-[0.875rem]">
                {submitting ? 'Signing in…' : 'Sign In'}
                {!submitting ? (
                  <ArrowRight className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                ) : null}
              </Button>
            </form>

            <div className="my-3 flex items-center gap-2.5 sm:my-3.5">
              <span className="h-px flex-1 bg-[#e2e8f0]" aria-hidden />
              <span className="text-[0.75rem] text-[#94a3b8]">or</span>
              <span className="h-px flex-1 bg-[#e2e8f0]" aria-hidden />
            </div>

            <Button
              type="button"
              variant="outline"
              className="h-9 w-full rounded-lg bg-white text-[0.75rem] font-medium hover:bg-[#f0f9ff] sm:h-10 sm:text-[0.8125rem]"
              style={{
                borderColor: `rgba(${BRAND_BLUE_RGB}, 0.35)`,
                color: BRAND_BLUE,
              }}>
              <Shield className="h-4 w-4" strokeWidth={2} aria-hidden />
              Contact Administrator for Access
            </Button>

            <p className="m-0 mt-3 text-center text-[0.6875rem] text-[#94a3b8]">
              © 2026 QTraceAI. All rights reserved.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AuthenticationPage;
